import { makeInMemoryAdapter } from "@livestore/adapter-web";
import { LiveStoreProvider, useStore } from "@livestore/react";
import type React from "react";
import { createContext, useContext, useMemo } from "react";
import { unstable_batchedUpdates as batchUpdates } from "react-dom";

import { ProvideAppDevStateRenderers } from "#scripts/lib/dev/app-dev-state-renderers.tsx";
import { Footer } from "./components/Footer.js";
import { Header } from "./components/Header.js";
import { MainSection } from "./components/MainSection.js";
import { events, schema } from "./livestore/schema.js";
import { type TodoListVM, createTodoListScope } from "./scope.js";

const TodoVMContext = createContext<TodoListVM | null>(null);

export const useTodoVM = () => {
  const vm = useContext(TodoVMContext);
  if (!vm) throw new Error("useTodoVM must be used within TodoVMProvider");
  return vm;
};

const TodoVMProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { store } = useStore();
  const vm = useMemo(() => {
    store.commit(
      events.todoCreated({ id: "todo_1", text: "🧺 Sort the whites" }),
      events.todoCreated({ id: "todo_2", text: "🌀 Start the washing machine" }),
      events.uiStateSet({ newTodoText: "🧴 Buy fabric softener" }),
    );
    return createTodoListScope(store);
  }, [store]);
  return <TodoVMContext.Provider value={vm}>{children}</TodoVMContext.Provider>;
};

const AppBody: React.FC = () => (
  <TodoVMProvider>
    <section data-loc="phosphor-blog/content/_sources/view-model-pattern/todo-vm/Root.tsx:37:14" data-name="section" className="todos">
      <Header />
      <MainSection />
      <Footer />
    </section>
  </TodoVMProvider>
);

const resetPersistence = import.meta.env.DEV && new URLSearchParams(window.location.search).get("reset") !== null;

if (resetPersistence) {
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.delete("reset");
  window.history.replaceState(null, "", `${window.location.pathname}?${searchParams.toString()}`);
}

const adapter = makeInMemoryAdapter();

export const App: React.FC = () => (
  <LiveStoreProvider
    schema={schema}
    adapter={adapter}
    renderLoading={(_) => <div data-loc="phosphor-blog/content/_sources/view-model-pattern/todo-vm/Root.tsx:59:32" data-name="div" className="">Loading LiveStore ({_.stage})...</div>}
    batchUpdates={batchUpdates}
  >
    <ProvideAppDevStateRenderers>
      <AppBody />
    </ProvideAppDevStateRenderers>
  </LiveStoreProvider>
);
